import React, { useState, useEffect, useCallback } from 'react';
import {
  getCredentialTypes,
  createCredentialType,
  updateCredentialType,
  deleteCredentialType,
} from '../../services/api';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Button,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import CredentialTypes from './CredentialTypes';
import CreateCredentialType from './CreateCredentialType';
import EditCredentialType from './EditCredentialType';
import CredentialTemplates from './CredentialTemplates';

// Tab Panel component for tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credential-tabpanel-${index}`}
      aria-labelledby={`credential-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

// Main Manage Credentials component
const ManageCredentials = () => {
  // State for tracking active tab
  const [tabValue, setTabValue] = useState(0);
  
  // State for managing credential types
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for managing editing workflow
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCredentialType, setCurrentCredentialType] = useState(null);

  // Load credential types from the console backend (persisted in Postgres).
  const loadTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const rows = await getCredentialTypes();
      const transformedTypes = (rows || []).map((row, index) => ({
        id: row.id || String(index + 1),
        name: row.name,
        description: row.description || '',
        version: row.version || '1.0',
        issuer: row.default_issuer_did || '',
        signingCredentialId: row.default_signing_credential_id || '',
        context: row.contexts || ['https://www.w3.org/2018/credentials/v1'],
        claims: Array.isArray(row.claims_schema) ? row.claims_schema : [], // flat: [{key,label,type,required}]
      }));

      setCredentialTypes(transformedTypes);
    } catch (err) {
      console.error('Error fetching credential types:', err);
      setError('Failed to load credential types from the console backend.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle creating new credential type
  const handleCreateCredentialType = () => {
    setIsCreating(true);
  };
  
  // Handle editing credential type
  const handleEditCredentialType = (credentialType) => {
    setCurrentCredentialType(credentialType);
    setIsEditing(true);
  };
  
  // Persist a new/edited credential type to the backend, then refresh.
  const handleSaveCredentialType = async (credentialType) => {
    try {
      const payload = {
        id: credentialType.id,
        name: credentialType.name,
        description: credentialType.description,
        type_name: credentialType.type_name || credentialType.name,
        contexts: credentialType.context || ['https://www.w3.org/2018/credentials/v1'],
        claims_schema: credentialType.claims || [],
        default_issuer_did: credentialType.issuer || null,
        default_signing_credential_id: credentialType.signingCredentialId || null,
      };
      if (isEditing && credentialType.id) {
        await updateCredentialType(credentialType.id, payload);
        setIsEditing(false);
      } else {
        await createCredentialType(payload);
        setIsCreating(false);
      }
      setCurrentCredentialType(null);
      await loadTypes();
    } catch (err) {
      console.error('Error saving credential type:', err);
      setError('Failed to save credential type. Make sure you are signed in.');
    }
  };
  
  // Handle canceling create/edit
  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    setCurrentCredentialType(null);
  };
  
  // Delete a credential type from the backend, then refresh.
  const handleDeleteCredentialType = async (id) => {
    try {
      await deleteCredentialType(id);
      await loadTypes();
    } catch (err) {
      console.error('Error deleting credential type:', err);
      setError('Failed to delete credential type. Make sure you are signed in.');
    }
  };
  
  // Render content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      );
    }

    if (isCreating) {
      return (
        <CreateCredentialType
          onSave={handleSaveCredentialType}
          onCancel={handleCancel}
        />
      );
    }

    if (isEditing && currentCredentialType) {
      return (
        <EditCredentialType
          credentialType={currentCredentialType}
          onSave={handleSaveCredentialType}
          onCancel={handleCancel}
        />
      );
    }

    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" fontFamily="Cousine, monospace" color="primary.main">
              Credential Types
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define and manage the credential types used in your system
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateCredentialType}
              sx={{ mt: { xs: 2, sm: 0 } }}
            >
              Create New Credential Type
            </Button>
          </Grid>
        </Grid>

        <CredentialTypes
          credentialTypes={credentialTypes}
          onEdit={handleEditCredentialType}
          onDelete={handleDeleteCredentialType}
        />
      </Box>
    );
  };
  
  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="credential management tabs">
          <Tab label="Credential Types" />
          <Tab label="Templates" />
          <Tab label="W3C Standards" />
          <Tab label="Settings" disabled />
        </Tabs>
      </Paper>
      
      <TabPanel value={tabValue} index={0}>
        {renderContent()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <CredentialTemplates credentialTypes={credentialTypes} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <Box>
          <Typography variant="h5" fontFamily="Cousine, monospace" color="primary.main" gutterBottom>
            W3C Verifiable Credentials Standard
          </Typography>
          <Typography variant="body1" paragraph>
            Our platform implements the W3C Verifiable Credentials Data Model 1.0, which is a standard for expressing credentials on the Web in a way that is cryptographically secure, privacy respecting, and machine-verifiable.
          </Typography>
          
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Standard VC Fields</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Chip icon={<InfoIcon />} label="@context" color="primary" variant="outlined" sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    JSON-LD context definition that establishes the credential's terms and definitions.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip icon={<InfoIcon />} label="type" color="primary" variant="outlined" sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    Multiple credential types that define the credential's purpose and schema.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip icon={<InfoIcon />} label="id" color="primary" variant="outlined" sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    Unique credential identifier, typically a URI that uniquely identifies this credential.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip icon={<InfoIcon />} label="issuer" color="primary" variant="outlined" sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    The DID of the credential issuer who attests to the credential's claims.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip icon={<InfoIcon />} label="issuanceDate" color="primary" variant="outlined" sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    ISO 8601 datetime when the credential was issued.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip icon={<InfoIcon />} label="expirationDate" color="primary" variant="outlined" sx={{ mb: 1 }} />
                  <Typography variant="body2">
                    Optional ISO 8601 datetime when the credential expires.
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Credential Subject</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                The credentialSubject contains claims about the subject of the credential.
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Multiple attributes in credentialSubject</li>
                <li>Support for nested objects and arrays</li>
                <li>Proper ID and identifier handling</li>
                <li>Custom claim structures based on domain-specific needs</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                {`{
  "credentialSubject": {
    "id": "did:example:ebfeb1f712ebc6f1c276e12ec21",
    "name": "Jane Doe",
    "degree": {
      "type": "BachelorDegree",
      "name": "Bachelor of Science in Mechanical Engineering"
    }
  }
}`}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Credential Status</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                The credentialStatus provides a way to check if the credential has been revoked or suspended.
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Status tracking mechanism</li>
                <li>Type of status list</li>
                <li>Reference to status verification endpoint</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                {`{
  "credentialStatus": {
    "id": "https://example.edu/credentials/status/3",
    "type": "CredentialStatusList2017"
  }
}`}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Proof</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                The proof provides cryptographic verification of the credential's authenticity and integrity.
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Signature type (e.g., Ed25519Signature2020)</li>
                <li>Created timestamp</li>
                <li>Proof purpose</li>
                <li>Verification method</li>
                <li>Proof value (the actual signature)</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                {`{
  "proof": {
    "type": "Ed25519Signature2020",
    "created": "2023-04-15T18:56:59Z",
    "proofPurpose": "assertionMethod",
    "verificationMethod": "https://example.edu/issuers/565049#key-1",
    "proofValue": "z58DAdFfa9SkqZMVPxAQpic6FNGKdFwVEAzQmzMHFzW1CmfcC8zrEHHLuVGfKqcYMKrJg2GSzCd4NgYZRDnhscnZ"
  }
}`}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Complete Example</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1, overflowX: 'auto' }}>
                {`{
  "@context": [
    "https://www.w3.org/2018/credentials/v1",
    "https://www.w3.org/2018/credentials/examples/v1"
  ],
  "id": "http://example.edu/credentials/3732",
  "type": ["VerifiableCredential", "UniversityDegreeCredential"],
  "issuer": "https://example.edu/issuers/565049",
  "issuanceDate": "2022-12-01T19:23:24Z",
  "credentialSubject": {
    "id": "did:example:ebfeb1f712ebc6f1c276e12ec21",
    "degree": {
      "type": "BachelorDegree",
      "name": "Bachelor of Science and Arts",
      "institution": "Example University",
      "graduationDate": "2022-05-15"
    }
  },
  "credentialStatus": {
    "id": "https://example.edu/credentials/status/3",
    "type": "CredentialStatusList2017"
  },
  "proof": {
    "type": "Ed25519Signature2020",
    "created": "2022-12-01T19:23:24Z",
    "proofPurpose": "assertionMethod",
    "verificationMethod": "https://example.edu/issuers/565049#key-1",
    "proofValue": "z58DAdFfa9SkqZMVPxAQpic6FNGKdFwVEAzQmzMHFzW1CmfcC8zrEHHLuVGfKqcYMKrJg2GSzCd4NgYZRDnhscnZ"
  }
}`}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default ManageCredentials;